import { Dispatch, MiddlewareAPI } from 'redux'
import { push } from 'connected-react-router'
import { toast } from 'react-toastify'

import { handleGenericErrors } from '../../utils/Products/errorHandler'

import { apiRequest, apiRequests } from '../actions/api'
import {
  CANCEL_A_PRODUCT_REMOVAL,
  CANCEL_A_PRODUCT_REMOVAL_ERROR,
  CANCEL_A_PRODUCT_REMOVAL_SUCCESS,
  CREATE_BRAND,
  CREATE_BRAND_ERROR,
  CREATE_BRAND_SUCCESS,
  CREATE_NOTICE,
  CREATE_NOTICE_ERROR,
  CREATE_NOTICE_SUCCESS,
  GET_A_PRODUCT,
  GET_A_PRODUCT_ERROR,
  GET_A_PRODUCT_SUCCESS,
  GET_BRANDS,
  GET_BRANDS_ERROR,
  GET_BRANDS_SUCCESS,
  GET_LOCALIZATIONS,
  GET_LOCALIZATIONS_ERROR,
  GET_LOCALIZATIONS_SUCCESS,
  SAVE_LOCALIZATIONS,
  GET_NOTICES,
  GET_NOTICES_ERROR,
  GET_NOTICES_SUCCESS,
  GET_PHOTOS,
  GET_PHOTOS_ERROR,
  GET_PHOTOS_SUCCESS,
  GET_PRODUCTS,
  GET_PRODUCTS_ERROR,
  GET_PRODUCTS_SUCCESS,
  INITIATE_A_PRODUCT_REMOVAL,
  INITIATE_A_PRODUCT_REMOVAL_ERROR,
  INITIATE_A_PRODUCT_REMOVAL_SUCCESS,
  MARK_NOTICES_REVIEWED,
  MARK_NOTICES_REVIEWED_ERROR,
  MARK_NOTICES_REVIEWED_SUCCESS,
  MERGE_PRODUCTS,
  MERGE_PRODUCTS_ERROR,
  MERGE_PRODUCTS_SUCCESS,
  POPULATE_BATCH_PRODUCTS,
  POST_A_PRODUCT,
  POST_A_PRODUCT_ERROR,
  POST_A_PRODUCT_SUCCESS,
  SAVE_LOCALIZATION_SUCCESS,
  SAVE_LOCALIZATION_ERROR,
  addBrand,
  getAProduct,
  getPhotos,
  setBatchPhotos,
  setBatchProducts,
  setBrands,
  setDeleteLoading,
  setLocalizations,
  setMarkedReviewedLoading,
  setMergeProductsLoading,
  setNotices,
  setNoticesReviewedStatus,
  setPhotos,
  setProducts,
  setLocalizationStatus,
  markNoticesReviewed,
} from '../actions/products'
import {
  addBulkEditError,
  addBulkEditSuccess,
  batchListMoveDown,
  setBatchLookupStage,
  setBatchProgressError,
  setClipboardProduct,
  setCurrentBatchId,
  setGettingProductId,
  setInitialProductValues,
  setIsLoading,
  setIsPostingProduct,
  setLastAction,
  setLookupValues,
  updateBatchProgress,
} from '../actions/ui'
import { Alias, LookProductApiResponse, ProductsActionTypes } from '../types/products'
import {
  API_BRANDS,
  API_LOCALIZATIONS,
  API_PRODUCT_MERGE,
  API_PRODUCT_NOTICES,
  API_PRODUCTS,
  LINEAGE_ID_BY_KEY,
  PATH_PRODUCTS,
  PRODUCT_TYPE_ID_BY_KEY,
} from '../../consts'
import { getInitialLookupValues, getInitialProductFormValues } from '../reducers/ui'
import {
  getProductAliases,
  setProductAliases,
  updateBatchAliasAssignments,
} from '../actions/productAliases'
import { serializeLookupValues } from '../../utils/lookupValues'
import { NoticeToMarkReviewed } from '../types/ui'
import { ApiLocalization } from '../../types'

const getProductsMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_PRODUCTS) {
      const { values, searchFunc, brandSearchMode, isBatchLookupMode, enableDeepMatcher } =
        action.payload
      dispatch(
        apiRequest({
          url: API_PRODUCTS,
          entity: 'products',
          withCredentials: true,
          params: serializeLookupValues(
            values,
            searchFunc,
            brandSearchMode,
            isBatchLookupMode,
            enableDeepMatcher
          ),
          onSuccess: GET_PRODUCTS_SUCCESS,
          onError: GET_PRODUCTS_ERROR,
          originalPayload: action.payload,
        })
      )
    }

    return result
  }

const getBrandsMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_BRANDS) {
      dispatch(
        apiRequest({
          url: API_BRANDS,
          entity: 'products',
          withCredentials: true,
          onSuccess: GET_BRANDS_SUCCESS,
          onError: GET_BRANDS_ERROR,
          originalPayload: action.payload,
        })
      )
    }

    return result
  }

const getNoticesMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_NOTICES) {
      dispatch(
        apiRequest({
          url: API_PRODUCT_NOTICES,
          entity: 'products',
          withCredentials: true,
          onSuccess: GET_NOTICES_SUCCESS,
          onError: GET_NOTICES_ERROR,
          originalPayload: action.payload,
        })
      )
    }

    return result
  }

const getNoticesSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch

    if (action.type === GET_NOTICES_SUCCESS) {
      dispatch(setIsLoading({ status: false }))
      dispatch(
        setNotices({
          notices: action.payload.data.data.notices,
          productIdToUuid: action.payload.data.data.productIdToUuid,
          noticesLoading: action.payload.data.data.noticesLoading,
        })
      )
    }
    return result
  }

const getNoticesErrorMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)
    if (action.type === GET_NOTICES_ERROR) {
      dispatch(setIsLoading({ status: false }))
      if (!toast.isActive('get-notices-error')) {
        toast('Error getting notices!', {
          containerId: 'error',
          toastId: 'get-notices-error',
        })
      }
    }
    return result
  }

const createBrandMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === CREATE_BRAND) {
      dispatch(
        apiRequest({
          url: API_BRANDS,
          method: 'post',
          entity: 'products',
          withCredentials: true,
          onSuccess: CREATE_BRAND_SUCCESS,
          onError: CREATE_BRAND_ERROR,
          originalPayload: action.payload,
          data: { name: action.payload.name },
          headers: { 'content-type': 'application/json' },
        })
      )
    } else if (action.type === CREATE_BRAND_SUCCESS) {
      dispatch(addBrand({ brand: action.payload.data.product_brand }))
    } else if (action.type === CREATE_BRAND_ERROR) {
      alert('An error has occurred...')
    }

    return result
  }

const markNoticesReviewedMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)
    if (action.type === MARK_NOTICES_REVIEWED) {
      dispatch(
        setMarkedReviewedLoading({
          notices: action.payload.notices.map((notice: NoticeToMarkReviewed) => ({
            ...notice,
            mark_reviewed_loading: true,
          })),
        })
      )
      dispatch(
        apiRequest({
          url: API_PRODUCT_NOTICES,
          method: 'patch',
          entity: 'products',
          withCredentials: true,
          onSuccess: MARK_NOTICES_REVIEWED_SUCCESS,
          onError: MARK_NOTICES_REVIEWED_ERROR,
          originalPayload: action.payload,
          headers: { 'content-type': 'application/json' },
          data: action.payload,
        })
      )
    }
    return result
  }

const createNoticeMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)
    if (action.type === CREATE_NOTICE) {
      dispatch(
        apiRequest({
          url: API_PRODUCT_NOTICES,
          method: 'post',
          entity: 'products',
          withCredentials: true,
          onSuccess: CREATE_NOTICE_SUCCESS,
          onError: CREATE_NOTICE_ERROR,
          originalPayload: action.payload,
          headers: { 'content-type': 'application/json' },
          data: action.payload,
        })
      )
    }
    return result
  }

const createNoticeSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)

    const dispatch = store.dispatch
    if (action.type === CREATE_NOTICE_SUCCESS) {
      toast('Notice created!', {
        containerId: 'default',
        toastId: 'create-notice',
      })
      dispatch(
        setNotices({
          notices: action.payload.data.data.notices,
          productIdToUuid: action.payload.data.data.productIdToUuid,
          noticesLoading: action.payload.data.data.noticesLoading,
        })
      )
    }
    return result
  }

const createNoticeErrorMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === CREATE_NOTICE_ERROR) {
      toast('Error while creating notice', {
        containerId: 'error',
        toastId: 'create-notice',
      })
    }
    return result
  }

const getLocalizationsMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)
    if (action.type === GET_LOCALIZATIONS) {
      dispatch(
        apiRequest({
          url: `${API_LOCALIZATIONS}/${action.payload.productId}`,
          method: 'get',
          entity: 'products',
          withCredentials: true,
          onSuccess: GET_LOCALIZATIONS_SUCCESS,
          onError: GET_LOCALIZATIONS_ERROR,
          originalPayload: action.payload,
          headers: { 'content-type': 'application/json' },
        })
      )
    }
    return result
  }

const getLocalizationsSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch
    if (action.type === GET_LOCALIZATIONS_SUCCESS) {
      dispatch(
        setLocalizations({
          localizations: action.payload.data.data.localizations,
          productUuid: action.payload.data.data.product_uuid,
          productId: action.payload.data.data.product_id,
        })
      )
    }
    return result
  }

const saveLocalizationsMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)
    if (action.type === SAVE_LOCALIZATIONS) {
      const { localizations, productId } = action.payload

      const toPatch = localizations[productId].filter((each: ApiLocalization) => {
        return each.id && each.id.length > 0
      })
      const toPost = localizations[productId].filter((each: ApiLocalization) => !each.id)

      toPatch.forEach((localization: ApiLocalization) => {
        dispatch(
          apiRequest({
            url: `${API_LOCALIZATIONS}/${localization.id}`,
            method: 'patch',
            entity: 'products',
            withCredentials: true,
            onSuccess: SAVE_LOCALIZATION_SUCCESS,
            onError: SAVE_LOCALIZATION_ERROR,
            originalPayload: localization,
            headers: { 'content-type': 'application/json' },
            data: localization,
          })
        )
      })

      toPost.forEach((localization: ApiLocalization) => {
        dispatch(
          apiRequest({
            url: `${API_LOCALIZATIONS}`,
            method: 'post',
            entity: 'products',
            withCredentials: true,
            onSuccess: SAVE_LOCALIZATION_SUCCESS,
            onError: SAVE_LOCALIZATION_ERROR,
            originalPayload: localization,
            headers: { 'content-type': 'application/json' },
            data: localization,
          })
        )
      })
    }
    return result
  }

const saveLocalizationSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch
    if (action.type === SAVE_LOCALIZATION_SUCCESS) {
      if (action.payload.data.productLocalizationId) {
        dispatch(
          setLocalizationStatus({
            newProductLocalizationId: action.payload.data.productLocalizationId,
            country: action.meta.originalPayload.country,
            subdivision: action.meta.originalPayload.subdivision,
            success: true,
            failure: false,
            updated: new Date(),
          })
        )
      } else {
        dispatch(
          setLocalizationStatus({
            country: action.meta.originalPayload.country,
            subdivision: action.meta.originalPayload.subdivision,
            originalLocalizationId: action.meta.originalPayload.id,
            success: true,
            failure: false,
            updated: new Date(),
          })
        )
      }
    }
    return result
  }

const saveLocalizationErrorMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)

    const dispatch = store.dispatch
    if (action.type === SAVE_LOCALIZATION_ERROR) {
      if (action.meta?.originalPayload?.id) {
        dispatch(
          setLocalizationStatus({
            country: action.meta.originalPayload.country,
            subdivision: action.meta.originalPayload.subdivision,
            originalLocalizationId: action.meta.originalPayload.id,
            success: false,
            failure: true,
            updated: new Date(),
          })
        )
      } else {
        setLocalizationStatus({
          country: action.meta.originalPayload.country,
          subdivision: action.meta.originalPayload.subdivision,
          success: false,
          failure: true,
          updated: new Date(),
        })
      }
    }
    return result
  }

const mergeProductsMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)
    if (action.type === MERGE_PRODUCTS) {
      dispatch(
        setMergeProductsLoading({
          product_uuid: action.payload.duplicate_product_id,
          loading: true,
        })
      )
      dispatch(
        apiRequest({
          url: API_PRODUCT_MERGE,
          method: 'put',
          entity: 'products',
          withCredentials: true,
          onSuccess: MERGE_PRODUCTS_SUCCESS,
          onError: MERGE_PRODUCTS_ERROR,
          originalPayload: action.payload,
          headers: { 'content-type': 'application/json' },
          data: action.payload,
        })
      )
    }
    return result
  }

const mergeProductsSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch

    if (action.type === MERGE_PRODUCTS_SUCCESS) {
      toast(`Product merge initiated, the request will be processed shortly.`, {
        containerId: 'default',
      })
      dispatch(
        setMergeProductsLoading({
          product_uuid: action.meta.originalPayload.duplicate_product_id,
          loading: false,
        })
      )

      const notice = action.meta.originalPayload.notice

      dispatch(
        markNoticesReviewed({
          notices: [
            {
              product_uuid: notice.product_uuid,
              kind: notice.kind,
            },
          ],
        })
      )
    }
    return result
  }

const mergeProductsErrorMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const toastId = 'merge-products-error'
    const dispatch = store.dispatch
    if (action.type === MERGE_PRODUCTS_ERROR) {
      dispatch(
        setMergeProductsLoading({
          product_uuid: action.meta.originalPayload.duplicate_product_id,
          loading: false,
        })
      )

      if (!toast.isActive(toastId)) {
        handleGenericErrors(action.payload, 'Error encountered while merging products')
      }
    }
    return result
  }

const markNoticesReviewedSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch
    if (action.type === MARK_NOTICES_REVIEWED_SUCCESS) {
      const notices = action.payload.data.data
      dispatch(setNoticesReviewedStatus({ notices }))
    }
    return result
  }

const markNoticesReviewedErrorMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const toastId = 'mark-notice-reviewed-error'
    if (action.type === MARK_NOTICES_REVIEWED_ERROR) {
      if (!toast.isActive(toastId)) {
        toast('Error encountered while marking notice(s) as reviewed!', {
          containerId: 'error',
          toastId,
        })
      }
    }
    return result
  }

const getBrandsResultsMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch
    const toastId = 'get-brands'

    if (action.type === GET_BRANDS_SUCCESS) {
      dispatch(setBrands({ brands: action.payload.data.product_brands }))
    } else if (action.type === GET_BRANDS_ERROR) {
      if (!toast.isActive(toastId)) {
        toast('Error occurred while fetching brands..', {
          containerId: 'error',
          toastId,
        })
      }
    }

    return result
  }

const getProductsSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch

    if (action.type === GET_PRODUCTS_SUCCESS) {
      const products = action.payload.data.data

      if (products.length > 0) {
        const productIds = products.map((product: LookProductApiResponse) => {
          return product.id.toString()
        })

        if (action.meta.originalPayload.batchId) {
          const batchId = action.meta.originalPayload.batchId
          dispatch(
            setBatchProducts({
              products: products,
              batchId: batchId,
            })
          )
          dispatch(getProductAliases({ productIds: productIds, batchId: batchId }))
          dispatch(getPhotos({ productIds: productIds, batchId: batchId }))
        } else {
          dispatch(setProducts({ products: products }))
          dispatch(getProductAliases({ productIds: productIds }))
          dispatch(getPhotos({ productIds: productIds }))
        }
      } else {
        if (action.meta.originalPayload.batchId) {
          dispatch(
            updateBatchProgress({
              batchId: action.meta.originalPayload.batchId,
              increment: 1,
            })
          )
        } else {
          dispatch(setIsLoading({ status: false }))
        }
      }
    }

    return result
  }

const getBatchProductsMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === POPULATE_BATCH_PRODUCTS) {
      const batchId = action.payload.batchId
      dispatch(setLookupValues({ values: getInitialLookupValues(), batchId: batchId }))
      dispatch(setProducts({ products: [], batchId: batchId }))
      dispatch(setProductAliases({ aliases: [], batchId: batchId }))
      dispatch(setPhotos({ productPhotosResponse: [], batchId: batchId }))
      dispatch(setCurrentBatchId({ batchId: batchId }))
    }

    return result
  }

const getProductsErrorMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_PRODUCTS_ERROR) {
      dispatch(setIsLoading({ status: false }))

      if (!toast.isActive('get-products-error')) {
        toast('Get products error', {
          containerId: 'error',
          toastId: 'get-products-error',
        })
      }

      if (action.meta.originalPayload.batchId) {
        dispatch(
          updateBatchProgress({
            batchId: action.meta.originalPayload.batchId,
            increment: 1,
          })
        )
        dispatch(setBatchProgressError({ batchId: action.meta.originalPayload.batchId }))
      }
    }

    return result
  }

const productMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_A_PRODUCT) {
      dispatch(setGettingProductId({ productId: action.payload.productId }))
      dispatch(setLastAction({ mode: action.payload.mode }))
      dispatch(
        apiRequest({
          url: `${API_PRODUCTS}/${action.payload.productId}`,
          entity: 'products',
          withCredentials: true,
          originalPayload: action.payload,
          onSuccess: GET_A_PRODUCT_SUCCESS,
          onError: GET_A_PRODUCT_ERROR,
        })
      )
    }

    if (action.type === POST_A_PRODUCT) {
      const { formData, mode } = action.payload
      const url = mode === 'edit' ? `${API_PRODUCTS}/${formData.get('id')}` : API_PRODUCTS

      dispatch(setIsPostingProduct({ status: true }))
      dispatch(
        apiRequest({
          url: url,
          method: mode === 'edit' ? 'PATCH' : 'POST',
          entity: 'products',
          headers: { 'content-type': 'multipart/form-data' },
          withCredentials: true,
          data: formData,
          originalPayload: action.payload,
          onSuccess: POST_A_PRODUCT_SUCCESS,
          onError: POST_A_PRODUCT_ERROR,
        })
      )
    }

    if (action.type === INITIATE_A_PRODUCT_REMOVAL) {
      const { productId } = action.payload
      const url = `${API_PRODUCTS}/${productId}/removal`

      dispatch(setIsPostingProduct({ status: true }))
      dispatch(
        apiRequest({
          url: url,
          method: 'POST',
          entity: 'productRemovals',
          withCredentials: true,
          originalPayload: action.payload,
          onSuccess: INITIATE_A_PRODUCT_REMOVAL_SUCCESS,
          onError: INITIATE_A_PRODUCT_REMOVAL_ERROR,
        })
      )
    }

    if (action.type === CANCEL_A_PRODUCT_REMOVAL) {
      const { productId } = action.payload
      const url = `${API_PRODUCTS}/${productId}/removal`

      dispatch(setIsPostingProduct({ status: true }))
      dispatch(
        apiRequest({
          url: url,
          method: 'DELETE',
          entity: 'productRemovals',
          withCredentials: true,
          originalPayload: action.payload,
          onSuccess: INITIATE_A_PRODUCT_REMOVAL_SUCCESS,
          onError: INITIATE_A_PRODUCT_REMOVAL_ERROR,
        })
      )
    }

    return result
  }

const getAProductSuccessMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_A_PRODUCT_SUCCESS) {
      if (!action.payload.data?.product) {
        return
      }

      const productData = action.payload.data.product
      const { aliases } = productData
      const { mode, alias: presetAlias, isBatchLookup } = action.meta.originalPayload

      if (mode === 'clipboard') {
        dispatch(setClipboardProduct({ product: productData }))
        dispatch(setLastAction({ mode: 'lookup' }))
        dispatch(setIsLoading({ status: false }))
        dispatch(setGettingProductId({ productId: undefined }))
        dispatch(setInitialProductValues({ values: {} }))

        const tableElement = document.getElementById('product-clipboard-table') as HTMLElement
        const inputField = document.createElement('input')
        inputField.type = 'text'
        inputField.value = tableElement.outerHTML
        inputField.readOnly = true
        inputField.style.position = 'fixed'
        inputField.style.top = '-999px'
        document.body.appendChild(inputField)

        inputField.select()
        document.execCommand('copy')
        inputField.remove()

        toast(`${productData.name} copied`, {
          containerId: 'default',
        })
        return result
      }

      if (mode === 'create' || mode === 'edit') {
        const initialProductValues = getInitialProductFormValues()
        Object.keys(initialProductValues).forEach((key: string) => {
          if (productData[key]) {
            initialProductValues[key] = productData[key]
          }
        })

        initialProductValues['aliases_attributes'] = presetAlias
          ? [{ name: presetAlias }]
          : aliases && mode === 'edit'
          ? aliases.map((alias: Alias) => ({
              id: alias.id,
              name: alias.name,
              _destroy: false,
            }))
          : []

        dispatch(setInitialProductValues({ values: initialProductValues }))
        dispatch(setLastAction({ mode: mode }))
        dispatch(setIsLoading({ status: false }))
        dispatch(setGettingProductId({ productId: undefined }))

        if (mode === 'create' && isBatchLookup) {
          dispatch(setBatchLookupStage({ stage: 'create' }))
        }

        if (!window.location.pathname.includes(PATH_PRODUCTS) && !isBatchLookup) {
          dispatch(push(`${PATH_PRODUCTS}/${mode}`))
        }
      } else {
        const formattedLookupProduct = {
          attributes: {
            ...productData,
            brand_id: productData.product_brand_id,
            lineage: LINEAGE_ID_BY_KEY[productData.lineage],
            product_type: PRODUCT_TYPE_ID_BY_KEY[productData.kind],
            product_subtype: productData.root_subtype,
            url: `${process.env.REACT_APP_WEB_HOST}/products/${productData.id}/${productData.url_slug}`,
          },
          id: productData.id,
          score: 100,
        }

        dispatch(setProducts({ products: [formattedLookupProduct] }))
        dispatch(setLastAction({ mode: mode }))
        dispatch(getProductAliases({ productIds: [productData.id.toString()] }))
        dispatch(getPhotos({ productIds: [productData.id.toString()] }))
        dispatch(setIsLoading({ status: false }))
        dispatch(setGettingProductId({ productId: undefined }))
      }
    }

    return result
  }

const getAProductErrorMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_A_PRODUCT_ERROR) {
      dispatch(setIsLoading({ status: false }))

      if (!toast.isActive('get-product-error')) {
        toast('Get product error', {
          containerId: 'error',
          toastId: 'get-product-error',
        })
      }
    }

    return result
  }

const postAProductSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductsActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch
    const currentBatchId: string = store.getState().ui.currentBatchId

    if (action.type === POST_A_PRODUCT_SUCCESS) {
      const mode = action.meta.originalPayload.mode
      const isBatchLookup = action.meta.originalPayload.isBatchLookup
      const isBulkEdit = action.meta.originalPayload.isBulkEdit
      const createdProduct = action.payload.data.product

      dispatch(setIsPostingProduct({ status: false }))

      if (isBulkEdit) {
        dispatch(addBulkEditSuccess({ id: createdProduct.id }))
      }

      if (isBatchLookup) {
        dispatch(
          updateBatchAliasAssignments({
            batchId: currentBatchId,
            batchSelectedProduct: {
              attributes: {
                ...createdProduct,
                url: `${process.env.REACT_APP_WEB_HOST}/products/${createdProduct.id}/${createdProduct.url_slug}`,
              },
              id: createdProduct.id,
              score: 100,
            },
            batchSelectedProductPhotos: createdProduct.photos,
            batchSelectedProductAliases: createdProduct.aliases.map((aliasItem) => aliasItem.name),
            isConfirmed: true,
          })
        )
        dispatch(batchListMoveDown())
        dispatch(setBatchLookupStage({ stage: 'review' }))
        toast(`Alias saved (new product) with ID ${createdProduct.id}`, {
          containerId: 'default',
          onClick: () => copyProductIdToClipboard(createdProduct.id),
        })
      } else if (mode === 'edit') {
        dispatch(setProducts({ products: [] }))
        if (!isBulkEdit) {
          toast('Product updated', { containerId: 'default' })
          dispatch(getAProduct({ productId: createdProduct.id, mode }))
        }
      } else {
        toast(`Product created with ID ${createdProduct.id}`, {
          containerId: 'default',
          onClick: () => copyProductIdToClipboard(createdProduct.id),
        })
        dispatch(getAProduct({ productId: createdProduct.id, mode }))
      }
    }

    return result
  }

const productIdInMessage = (message: String) => {
  // "name is already taken by product 2210.".match(/name is already taken by product (\d+)\./)
  // returns: Array [ "is already taken by product 2210.", "2210" ]
  return message.match(/is already taken by product (\d+)/)
}

const getProductIdFromErrorMessage = (message: String) => {
  const canBuildLink = productIdInMessage(message)
  if (canBuildLink) {
    return parseInt(canBuildLink[1])
  }
  return null
}

const copyProductIdToClipboard = (id: number | null) => {
  if (id) {
    // https://hackernoon.com/copying-text-to-clipboard-with-javascript-df4d4988697f
    const el = document.createElement('textarea')
    // @ts-ignore
    el.value = id
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
    toast(`Product ID ${id} copied to clipboard!`, { containerId: 'default' })
  }
}

const postAProductErrorMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === POST_A_PRODUCT_ERROR) {
      const isBulkEdit = action.meta.originalPayload.isBulkEdit
      const validationMessage =
        action.payload.response?.data?.errors?.validations ||
        action.payload.response?.data?.exception ||
        ''

      dispatch(setIsPostingProduct({ status: false }))

      if (!isBulkEdit) {
        if (typeof validationMessage === 'object') {
          const firstKey = Object.keys(validationMessage)[0]
          const messageToDisplay = validationMessage[firstKey][0]
          toast(`Error: ${firstKey} ${messageToDisplay}`, {
            containerId: 'error',
            onClick: () => copyProductIdToClipboard(getProductIdFromErrorMessage(messageToDisplay)),
          })
        } else {
          copyProductIdToClipboard(getProductIdFromErrorMessage(validationMessage))
          toast(`Error: ${validationMessage}`, {
            containerId: 'error',
            onClick: () => copyProductIdToClipboard(validationMessage),
          })
        }
      } else {
        dispatch(
          addBulkEditError({
            // @ts-ignore
            id: parseInt(action.payload.config.url.split('/')[3]),
            message:
              typeof validationMessage === 'object'
                ? validationMessage[Object.keys(validationMessage)[0]]
                : validationMessage,
          })
        )
      }
    }

    return result
  }

const productRemovalResultMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === INITIATE_A_PRODUCT_REMOVAL_SUCCESS) {
      const { productId, notice } = action.meta.originalPayload

      toast(
        `Product #${productId} removal initiated, the request will be processed as soon as the product goes out-of-stock.`,
        {
          containerId: 'default',
        }
      )
      dispatch(setIsPostingProduct({ status: false }))

      if (notice) {
        dispatch(
          setDeleteLoading({
            productId,
            deleteIsLoading: false,
            removalInitiated: true,
            notice,
          })
        )

        dispatch(
          markNoticesReviewed({
            notices: [
              {
                product_uuid: notice.product_uuid,
                kind: notice.kind,
              },
            ],
          })
        )
      }
    } else if (action.type === INITIATE_A_PRODUCT_REMOVAL_ERROR) {
      const { productId, notice } = action.meta.originalPayload
      handleGenericErrors(
        action.payload,
        `Error: failed to initiate removal for product #${productId}`
      )

      dispatch(setIsPostingProduct({ status: false }))
      if (notice) {
        dispatch(
          setDeleteLoading({
            productId,
            deleteIsLoading: false,
            removalInitiated: false,
            notice,
          })
        )
      }
    } else if (action.type === CANCEL_A_PRODUCT_REMOVAL_SUCCESS) {
      const { productId } = action.meta.originalPayload

      toast(`Product #${productId} removal cancellation sent, will be processed shortly.`, {
        containerId: 'default',
      })
      dispatch(setIsPostingProduct({ status: false }))
    } else if (action.type === CANCEL_A_PRODUCT_REMOVAL_ERROR) {
      const { productId } = action.meta.originalPayload

      handleGenericErrors(
        action.payload,
        `Error: failed to cancel removal for product #${productId}`
      )
      dispatch(setIsPostingProduct({ status: false }))
    }

    return result
  }

const getPhotosMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_PHOTOS) {
      const urls = action.payload.productIds.map((pid) => ({
        address: `${API_PRODUCTS}/${pid}/photos`,
        urlMeta: {
          productId: pid,
        },
      }))

      dispatch(
        apiRequests({
          urls: urls,
          entity: 'products',
          originalPayload: action.payload,
          onSuccess: GET_PHOTOS_SUCCESS,
          onError: GET_PHOTOS_ERROR,
        })
      )
    }

    return result
  }

const getPhotosSuccessMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_PHOTOS_SUCCESS) {
      if (action.meta.originalPayload.batchId) {
        const batchId = action.meta.originalPayload.batchId
        dispatch(
          setBatchPhotos({
            productPhotosResponse: action.payload,
            batchId: batchId,
          })
        )
        dispatch(updateBatchProgress({ batchId: batchId, increment: 1 }))
      } else {
        dispatch(setPhotos({ productPhotosResponse: action.payload }))
        dispatch(setIsLoading({ status: false }))
      }
    }

    return result
  }

const getPhotosErrorMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductsActionTypes) => {
    const result = next(action)

    if (action.type === GET_PHOTOS_ERROR) {
      if (action.meta.originalPayload.batchId) {
        dispatch(
          updateBatchProgress({
            batchId: action.meta.originalPayload.batchId,
            increment: 1,
          })
        )
      } else {
        dispatch(setIsLoading({ status: false }))
      }

      if (!toast.isActive('get-photos-error')) {
        toast('Get photos error', {
          containerId: 'error',
          toastId: 'get-photos-error',
        })
      }
    }

    return result
  }

export const productsMiddlewares = [
  getProductsMiddleware,
  getProductsSuccessMiddleware,
  getProductsErrorMiddleware,
  getNoticesMiddleware,
  getNoticesSuccessMiddleware,
  getNoticesErrorMiddleware,
  productMiddleware,
  getAProductSuccessMiddleware,
  getAProductErrorMiddleware,
  postAProductSuccessMiddleware,
  postAProductErrorMiddleware,
  getPhotosMiddleware,
  getPhotosSuccessMiddleware,
  getPhotosErrorMiddleware,
  getBatchProductsMiddleware,
  productRemovalResultMiddleware,
  markNoticesReviewedMiddleware,
  markNoticesReviewedSuccessMiddleware,
  markNoticesReviewedErrorMiddleware,
  mergeProductsMiddleware,
  mergeProductsSuccessMiddleware,
  mergeProductsErrorMiddleware,
  getBrandsMiddleware,
  getBrandsResultsMiddleware,
  createBrandMiddleware,
  createNoticeMiddleware,
  createNoticeSuccessMiddleware,
  createNoticeErrorMiddleware,
  getLocalizationsMiddleware,
  getLocalizationsSuccessMiddleware,
  saveLocalizationsMiddleware,
  saveLocalizationSuccessMiddleware,
  saveLocalizationErrorMiddleware,
]
